import * as React from "react";
import { useState, useEffect, useContext } from "react";
import { Accordion, Button, Grid, Header, Icon, Label, Modal, Popup } from "semantic-ui-react";
import { parseDiff } from 'react-diff-view';
import { ConfigContext } from "../../../../context/ConfigContext";
import { diffLines, formatLines } from 'unidiff';
import { CompanyConfigurationDraft } from "../../../../../../generated";
import { backend } from "../../../../../../xconvert-backend";
import { authentication } from "../../../../../../authentication";
import { notify } from 'react-notify-toast';
import { CherryPickModal } from "../../CherryPickModal";
import { CustomDiff } from "../diff/CustomDiff";
import { updateConfig, workingConfig } from "../../ConfigSignal";

interface DraftModalProps {
    isOpen: boolean;
    onClose: (refresh: boolean) => void;
    draft: CompanyConfigurationDraft;
}

export function DraftModal({ isOpen, onClose, draft }: DraftModalProps) {
    const context = useContext(ConfigContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [comment, setComment] = useState("");
    const [response, setResponse] = useState(null);
    const [hunks, setHunks] = useState(null);
    const [type, setType] = useState(null);
    const [draftName, setDraftName] = useState("");
    const [writable, setWritable] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);
    const [showCompareModal, setShowCompareModal] = useState(false);

    useEffect(() => {
        if (isOpen && hunks === null) {
            const currentConfig = workingConfig.value;
            const newConfig = JSON.stringify(draft.config, null, 4);

            const diffText = formatLines(diffLines(currentConfig, newConfig), { context: 3 });
            const [diff] = parseDiff(diffText, { nearbySequences: 'zip' });

            const writable = (draft.userId === "" || draft.userId === "you");
            setHunks(diff.hunks);
            setWritable(writable);
        }
    }, [isOpen, hunks, draft]);

    function handleClose(refresh: boolean) {
        onClose(refresh);
    }

    function override() {
        updateConfig(JSON.stringify(draft.config, null, 4), true, false);
        handleClose(true);
    }

    function rebase() {
        const rebasedConfig = patchedConfig();
        updateConfig(rebasedConfig, true, false);
        handleClose(true);
    }

    async function handleDelete() {
        const auth = await backend.withTokenAuthHeader(authentication.token);
        const response = await backend.internalApi.deleteDraftOfConfig(context.companyId, draft._id, auth);
        if (response.status === 204) {
            notify.show('Deleted draft: ' + draft.name, 'success', 3000, '#28f751');
        } else {
            notify.show("ERROR: " + response.status + " : " + response.statusText, 'error', 5000, '#fc0303');
        }
        handleClose(false);
    }

    async function handleShare() {
        const auth = await backend.withTokenAuthHeader(authentication.token);
        const response = await backend.internalApi.updateConfigDraft(draft._id, !draft.shared, auth);

        if (response.newDraft !== undefined) {
            notify.show('Draft share: ' + response.newDraft.shared, 'success', 3000, '#28f751');
        } else {
            notify.show("ERROR: couldn't update draft, check logs.", 'error', 5000, '#fc0303');
        }
        handleClose(false);
    }

    function handleAccordionClick(e, titleProps) {
        const { index } = titleProps;
        const newIndex = activeIndex === index ? -1 : index;
        setActiveIndex(newIndex);
    }

    function patchedConfig() {
        const patchSet = draft.patchSet;
        return applyPatchSet(workingConfig.value, patchSet);
    }

    function applyPatchSet(config, patchSet) {
        const jp = require('jsonpath');
        let conf = JSON.parse(config);

        patchSet.map(patch => {
            let value = patch.value;
            if (value) {
                if (value.startsWith("[") || value.startsWith("{")) {
                    try {
                        value = JSON.parse(patch.value);
                    } catch (e) {
                        // probably no object
                    }
                }
            }
            const jsonPath = patch.path;
            jp.value(conf, jsonPath, value);
        });

        return JSON.stringify(conf, null, 4);
    }

    function drawOverride() {
        return (
            <p>
                <Grid padded="horizontally" stackable columns='equal' centered>
                    <Grid.Column>your current working copy:</Grid.Column>
                    <Grid.Column>this draft:</Grid.Column>
                </Grid>

                <CustomDiff
                    oldJson={workingConfig.value}
                    newJson={JSON.stringify(draft.config, null, 4)}
                />

                <br />
                <Popup content='overrides your current changes with this draft' trigger={
                    <Button id='DraftModalSaveButton' primary loading={isSubmitting} onClick={override}>Override</Button>
                } />
            </p>
        );
    }

    function drawRebase() {
        if (draft.patchSet === undefined) {
            return <Label color={'red'}>No PatchSet found! Most likely an older Draft.</Label>;
        }
        return (
            <p>
                <Grid padded="horizontally" stackable columns='equal' centered>
                    <Grid.Column>your current working copy:</Grid.Column>
                    <Grid.Column>this draft:</Grid.Column>
                </Grid>

                <CustomDiff
                    oldJson={workingConfig.value}
                    newJson={patchedConfig()}
                />

                <br />
                <Popup
                    content="Rebase only overrides the fields in your config, that have been changed for this draft. It's patching the config with its patchSet."
                    trigger={
                        <Button id='DraftModalSaveButton' primary loading={isSubmitting} onClick={rebase}>Rebase</Button>
                    } />
            </p>
        );
    }

    function drawAccordion() {
        return <div
            style={{
                overflow: 'auto',
                maxHeight: '900px',
                border: '1px solid #d4d4d5',
            }}
                >
            <Accordion styled fluid>
            <Accordion.Title
                active={activeIndex === 0}
                index={0}
                onClick={handleAccordionClick}
            >
                <Icon name='dropdown' />
                Override
            </Accordion.Title>
            <Accordion.Content active={activeIndex === 0}>
                {drawOverride()}
            </Accordion.Content>
            <Accordion.Title
                active={activeIndex === 1}
                index={1}
                onClick={handleAccordionClick}
            >
                <Icon name='dropdown' />
                Rebase
            </Accordion.Title>
            <Accordion.Content active={activeIndex === 1}>
                {drawRebase()}
            </Accordion.Content>
        </Accordion>
        </div>
    }

    return (
        <Modal
            open={isOpen}
            onClose={() => handleClose(false)}
            size='fullscreen'>
            <Header icon='browser' content={'Draft: ' + draft.name} />
            <Modal.Content>
                created at: {draft.createdAt}
                <br />
                <br />
                <Button id='DraftModalCancelButton' onClick={() => setShowCompareModal(true)}>CherryPick</Button>
                <br />
                <br />
                {drawAccordion()}

                <Button disabled={!writable} id='ShareModalDeleteButton' onClick={handleShare} color='yellow'>share/unshare</Button>
                <Button disabled={!writable} id='DraftModalDeleteButton' onClick={handleDelete} color='red'>Delete</Button>
                <Button id='DraftModalCancelButton' onClick={() => handleClose(false)}>Close</Button>

                {showCompareModal && <CherryPickModal
                    isOpen={showCompareModal}
                    left={workingConfig.value}
                    right={JSON.stringify(draft.config, null, 4)}
                    onSave={(newConfig) => {
                        updateConfig(newConfig, true, false).then(r => {
                            onClose(true);
                        });
                    }}
                    onClose={() => setShowCompareModal(false)}
                />}
            </Modal.Content>
        </Modal>
    );
}